import React, { useState } from 'react';
import { Form, Input, Button, Space, Modal, message, Upload } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Category, CategoryRequest } from '../../types/category';
import { categoryService } from '../../services/categoryService';

interface CategoryFormProps {
  category?: Category;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  isVisible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [categoryData, setCategoryData] = useState<Category | undefined>(category);
  const [uploadingHorizontal, setUploadingHorizontal] = useState<boolean>(false);
  const [uploadingVertical, setUploadingVertical] = useState<boolean>(false);
  const [horizontalImageDimensions, setHorizontalImageDimensions] = useState<{width: number, height: number} | null>(null);
  const [verticalImageDimensions, setVerticalImageDimensions] = useState<{width: number, height: number} | null>(null);
  const [horizontalImageKey, setHorizontalImageKey] = useState<number>(Date.now());
  const [verticalImageKey, setVerticalImageKey] = useState<number>(Date.now());

  const isEditing = !!category;
  const title = isEditing 
    ? t('categories.form.edit_title') 
    : t('categories.form.create_title');

  const handleSubmit = async (values: CategoryRequest) => {
    setIsLoading(true);
    try {
      if (isEditing && category) {
        // Preserve existing image fields when updating
        const updateData = {
          ...values,
          image_horizontal: categoryData?.image_horizontal || '',
          image_vertical: categoryData?.image_vertical || ''
        };
        await categoryService.updateCategory(category.id, updateData as any);
        message.success(t('categories.notifications.update_success'));
      } else {
        const newCategory = await categoryService.createCategory(values);
        setCategoryData(newCategory);
        message.success(t('categories.notifications.create_success'));
      }
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error submitting category form:', error);
      message.error(t('categories.notifications.error', { message: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle image upload
  const handleImageUpload = async (file: File, imageType: 'horizontal' | 'vertical') => {
    const categoryId = category?.id || categoryData?.id;
    if (!categoryId) {
      message.error(t('categories.form.save_before_upload', 'Please save the category before uploading images'));
      return false;
    }

    if (imageType === 'horizontal') {
      setUploadingHorizontal(true);
    } else {
      setUploadingVertical(true);
    }

    try {
      const formData = new FormData();
      formData.append('image', file);

      // Get auth token from localStorage
      const storedSession = localStorage.getItem('userSession');
      const headers: Record<string, string> = {};
      
      if (storedSession) {
        const session = JSON.parse(storedSession);
        headers['Authorization'] = `Bearer ${session.token}`;
      }

      const response = await fetch(`/api/categories/${categoryId}/upload/${imageType}`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      message.success(t('categories.form.upload_success', `${imageType} image uploaded successfully`));
      
      // Refresh category data to show the new image
      if (categoryId) {
        const refreshedCategoryData = await categoryService.getCategoryById(categoryId);
        setCategoryData(refreshedCategoryData);
        
        // Update cache key to force image refresh
        if (imageType === 'horizontal') {
          setHorizontalImageKey(Date.now());
        } else {
          setVerticalImageKey(Date.now());
        }
      }
      
      return true;
    } catch (error) {
      console.error('Upload error:', error);
      message.error(t('categories.form.upload_error', 'Failed to upload image'));
      return false;
    } finally {
      if (imageType === 'horizontal') {
        setUploadingHorizontal(false);
      } else {
        setUploadingVertical(false);
      }
    }
  };

  // Handle image deletion
  const handleImageDelete = async (imageType: 'horizontal' | 'vertical') => {
    const categoryId = category?.id || categoryData?.id;
    if (!categoryId) {
      message.error(t('categories.form.save_before_delete', 'Please save the category before deleting images'));
      return false;
    }

    if (imageType === 'horizontal') {
      setUploadingHorizontal(true);
    } else {
      setUploadingVertical(true);
    }

    try {
      // Get auth token from localStorage
      const storedSession = localStorage.getItem('userSession');
      const headers: Record<string, string> = {};
      
      if (storedSession) {
        const session = JSON.parse(storedSession);
        headers['Authorization'] = `Bearer ${session.token}`;
      }

      const response = await fetch(`/api/categories/${categoryId}/delete/${imageType}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      const result = await response.json();
      message.success(t('categories.form.delete_success', `${imageType} image deleted successfully`));
      
      // Refresh category data to remove the image
      if (categoryId) {
        const refreshedCategoryData = await categoryService.getCategoryById(categoryId);
        setCategoryData(refreshedCategoryData);
        
        // Update cache key to force image refresh
        if (imageType === 'horizontal') {
          setHorizontalImageKey(Date.now());
        } else {
          setVerticalImageKey(Date.now());
        }
      }
      
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      message.error(t('categories.form.delete_error', 'Failed to delete image'));
      return false;
    } finally {
      if (imageType === 'horizontal') {
        setUploadingHorizontal(false);
      } else {
        setUploadingVertical(false);
      }
    }
  };

  const handleImagePreviewClick = (imageUrl: string) => {
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  };

  const handleImageLoad = (imageType: 'horizontal' | 'vertical', event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.target as HTMLImageElement;
    const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
    
    if (imageType === 'horizontal') {
      setHorizontalImageDimensions(dimensions);
    } else {
      setVerticalImageDimensions(dimensions);
    }
  };

  // Fetch fresh category data when modal opens for editing
  React.useEffect(() => {
    const fetchCategoryData = async () => {
      if (isVisible && category?.id) {
        try {
          const freshCategory = await categoryService.getCategoryById(category.id);
          setCategoryData(freshCategory);
          form.setFieldsValue({
            name: freshCategory.name || '',
            secondary_name: freshCategory.secondary_name || '',
          });
        } catch (error) {
          console.error('Error fetching category:', error);
          // Fallback to provided category data
          setCategoryData(category);
          form.setFieldsValue({
            name: category?.name || '',
            secondary_name: category?.secondary_name || '',
          });
        }
      } else if (isVisible && !category) {
        // Creating new category
        setCategoryData(undefined);
        form.setFieldsValue({
          name: '',
          secondary_name: '',
        });
      }
    };

    fetchCategoryData();
  }, [form, category, isVisible]);

  return (
    <Modal
      title={title}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          name: category?.name || '',
          secondary_name: category?.secondary_name || '',
        }}
      >
        <Form.Item
          name="name"
          label={t('categories.form.name')}
          rules={[
            {
              required: true,
              message: t('categories.form.name_required'),
            },
          ]}
        >
          <Input placeholder={t('categories.form.name_placeholder')} />
        </Form.Item>

        <Form.Item
          name="secondary_name"
          label={t('categories.form.secondary_name')}
        >
          <Input placeholder={t('categories.form.secondary_name_placeholder')} />
        </Form.Item>

        {/* Image Horizontal Upload */}
        {isEditing && categoryData && (
          <>
            <Form.Item
              label={t('categories.form.image_horizontal', 'Image Horizontal')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Upload
                  name="image_horizontal"
                  accept=".png,.jpg,.jpeg"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleImageUpload(file, 'horizontal');
                    return false; // Prevent default upload
                  }}
                  disabled={uploadingHorizontal}
                >
                  <Button 
                    icon={<UploadOutlined />} 
                    loading={uploadingHorizontal}
                  >
                    {t('categories.form.upload_horizontal', 'Upload Horizontal Image')}
                  </Button>
                </Upload>
                {categoryData?.image_horizontal && categoryData.image_horizontal !== null && categoryData.image_horizontal !== "" && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img 
                      src={`${categoryData.image_horizontal}?t=${horizontalImageKey}`} 
                      alt="Horizontal preview" 
                      style={{ 
                        width: '80px', 
                        height: '40px', 
                        objectFit: 'cover', 
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                      onClick={() => handleImagePreviewClick(categoryData.image_horizontal!)}
                      onMouseEnter={(e) => (e.target as HTMLImageElement).style.opacity = '0.8'}
                      onMouseLeave={(e) => (e.target as HTMLImageElement).style.opacity = '1'}
                      onLoad={(e) => handleImageLoad('horizontal', e)}
                      title={t('categories.form.click_to_view_full', 'Click to view full size image')}
                    />
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {horizontalImageDimensions 
                        ? `${horizontalImageDimensions.width} x ${horizontalImageDimensions.height}`
                        : t('categories.form.current_image', 'Current image')
                      }
                    </span>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={uploadingHorizontal}
                      onClick={() => handleImageDelete('horizontal')}
                      title={t('categories.form.delete_image', 'Delete image')}
                    />
                  </div>
                )}
              </div>
            </Form.Item>

            {/* Image Vertical Upload */}
            <Form.Item
              label={t('categories.form.image_vertical', 'Image Vertical')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Upload
                  name="image_vertical"
                  accept=".png,.jpg,.jpeg"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleImageUpload(file, 'vertical');
                    return false; // Prevent default upload
                  }}
                  disabled={uploadingVertical}
                >
                  <Button 
                    icon={<UploadOutlined />} 
                    loading={uploadingVertical}
                  >
                    {t('categories.form.upload_vertical', 'Upload Vertical Image')}
                  </Button>
                </Upload>
                {categoryData?.image_vertical && categoryData.image_vertical !== null && categoryData.image_vertical !== "" && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img 
                      src={`${categoryData.image_vertical}?t=${verticalImageKey}`} 
                      alt="Vertical preview" 
                      style={{ 
                        width: '40px', 
                        height: '60px', 
                        objectFit: 'cover', 
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                      onClick={() => handleImagePreviewClick(categoryData.image_vertical!)}
                      onMouseEnter={(e) => (e.target as HTMLImageElement).style.opacity = '0.8'}
                      onMouseLeave={(e) => (e.target as HTMLImageElement).style.opacity = '1'}
                      onLoad={(e) => handleImageLoad('vertical', e)}
                      title={t('categories.form.click_to_view_full', 'Click to view full size image')}
                    />
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {verticalImageDimensions 
                        ? `${verticalImageDimensions.width} x ${verticalImageDimensions.height}`
                        : t('categories.form.current_image', 'Current image')
                      }
                    </span>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={uploadingVertical}
                      onClick={() => handleImageDelete('vertical')}
                      title={t('categories.form.delete_image', 'Delete image')}
                    />
                  </div>
                )}
              </div>
            </Form.Item>
          </>
        )}

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
            >
              {t('categories.form.submit')}
            </Button>
            <Button onClick={onClose}>
              {t('categories.form.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CategoryForm; 